import React, { useRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import styles from "./DragDropList.css";
import { useDrag, useDrop } from "react-dnd-cjs";
import DragDropItemTypes from "./DragDropItemTypes";

/**
 * adapted from
 * https://react-dnd.github.io/react-dnd/examples/sortable/simple
 */
export default function DragDropItem({
  itemId,
  index,
  moveItem,
  children,
  itemType,
  isDraggingClassName = styles.isDragging,
}) {
  const ref = useRef(null);
  const [{ handlerId }, drop] = useDrop({
    accept: itemType,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      // Determine rectangle on screen
      const hoverBoundingRect =
        ref.current && ref.current.getBoundingClientRect();
      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      // Time to actually perform the action
      moveItem(dragIndex, hoverIndex);
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    item: { type: itemType, id: itemId, index },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  return (
    <div
      ref={ref}
      data-handler-id={handlerId}
      className={classNames({ [isDraggingClassName]: isDragging })}
    >
      {children}
    </div>
  );
}

DragDropItem.propTypes = {
  itemId: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  moveItem: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  itemType: PropTypes.oneOf(Object.values(DragDropItemTypes)).isRequired,
  isDraggingClassName: PropTypes.string,
};
